/**
 * Cart utilities for managing cart operations
 * Adapted from 3in1CMS: bajajfinserv-web/ui.solid/src/lib/util/cart.ts
 */

/**
 * Interface for cart item details
 */
export interface ICartItemDetails {
  prod: string;
  amt: number;
  [key: string]: unknown;
}

/**
 * Interface for cart API response
 */
export interface ICartDetails {
  cartDetails: ICartItemDetails[];
  cartCount?: number;
}

/**
 * Type for cart product names
 */
/**
 * Product codes mapping
 */
export const PROD_CODES: Record<string, { value: string; types: Set<string> }> = {
  SALPL: {
    value: "SALPL",
    types: new Set([
      "SAL_NO_OFFER",
      "SAL_GIN",
      "SAL_PREAPPROVED",
      "RSL_LINE",
      "RSL_NO_OFFER",
      "RSL_PREAPPROVED",
    ]),
  },
  PLCS: {
    value: "PLCS",
    types: new Set([
      "PLCS_GIN",
      "PLCS_NORMAL",
      "RPL_FULL_UW",
      "RPL_LITE_UW",
      "RPL_STP",
      "RPPL_FUW",
      "PPL_PREQUALIFIED",
      "PLCS_PREQUALIFIED",
      "PLPPL_PREQUALIFIED",
      "PLCS_3PL",
    ]),
  },
  TWL: { value: "TWL", types: new Set(["TW_NTB", "TW_PTB", "TW_ETB"]) },
  UTL_ALL: { value: "UTL_ALL", types: new Set(["UTL_ALL"]) },
  UTL_LAT: { value: "UTL_LAT", types: new Set(["UTL_LAT"]) },
  UTL_BT: { value: "UTL_BT", types: new Set(["UTL_BT"]) },
  EMI: { value: "EMI", types: new Set(["EMI", "EMI-VC", "EMI-AC", "EMIPWA"]) },
  HC: { value: "HC", types: new Set(["EMI-HC"]) },
  DL: {
    value: "DL",
    types: new Set([
      "PRO_GCO_DL",
      "PRO_NTB_DL",
      "PRO_ETB_DL",
      "RPR_NTB_DL",
      "PRO_DL",
      "PRO_ICL_DL",
      "RPR_TOPUP",
    ]),
  },
  PEMFR: { value: "PEMFR", types: new Set(["PEMFR_NTB"]) },
  LFL: { value: "LFL", types: new Set(["PRO_LW", "PRO_NTB_LW", "PRO_ETB_LW"]) },
  BL: {
    value: "BL",
    types: new Set(["PRO_BL_NTB", "PSBL_NTB", "PSBL_OFFER", "PSBL", "PLBL_SLV", "PSBL_ICL"]),
  },
  PHARMA: { value: "PHARMA", types: new Set(["RPR_NTB_PHARMA"]) },
  CA: { value: "CA", types: new Set(["PRO_GCO_CA", "PRO_NTB_CA", "PRO_ETB_CA", "PRO_ICL_CA"]) },
  FD: {
    value: "FD",
    types: new Set([
      "FD",
      "HUFETBLTS",
      "HUFETBHTS",
      "HUFNTBLTS",
      "HUFNTBHTS",
      "FDNTBLTS",
      "FDNTBHTS",
      "FDETBLTS",
      "FDETBHTS",
      "SPNTBLTS",
      "SPNTBHTS",
      "SPETBLTS",
      "SPETBHTS",
      "EXPRESSFDHTS",
      "EXPRESSFDLTS",
      "MINORNTBLTS",
      "MINORNTBHTS",
      "MINORETBLTS",
      "MINORETBHTS",
    ]),
  },
  SDP: { value: "SDP", types: new Set(["SDP"]) },
  LAP: {
    value: "LAP",
    types: new Set([
      "SMELAP_ETB",
      "SMELAP_NTB",
      "SMELAP_OFFER",
      "LAPPROF_OFFER",
      "LAPPROF_ETB",
      "LAPPROF_NTB",
      "SMELAP_NTB_FRESH",
      "SMELAP_OFFER_FRESH",
      "LAPPROF_NTB_FRESH",
      "LAPPROF_OFFER_FRESH",
    ]),
  },
  LAPBT: {
    value: "LAPBT",
    types: new Set(["SMELAP_NTB_BT", "SMELAP_OFFER_BT", "LAPPROF_NTB_BT", "LAPPROF_OFFER_BT"]),
  },
  GLFRESH: { value: "GLFRESH", types: new Set(["GLFRESH"]) },
  GLBT: { value: "GLBT", types: new Set(["GLBT"]) },
  LAS_SHARES: { value: "LAS", types: new Set(["LAS_SHARES", "HTS_LAS"]) },
  LAS_MUTUALFUND: { value: "LAS", types: new Set(["LAS_MUTUALFUND", "HTS_LAMF"]) },
  HL: { value: "HL", types: new Set(["HL", "HL_NO_OFFER", "HL_FRESH", "HL_OFFER"]) },
  HL_BT: { value: "HLBT", types: new Set(["HL_BT_NO_OFFER", "HL_BT_OFFER", "HL_BT"]) },
  BLSE: {
    value: "BLSE",
    types: new Set(["BLSE_OFFER", "BLSE_NTB", "PLSE_NTB", "PLSE_OFFER", "MBLS_NTB", "MBLS_OFFER"]),
  },
  BLSEBT: {
    value: "BLSEBT",
    types: new Set([
      "BLSE_OFFER_BT",
      "BLSE_NTB_BT",
      "PLSE_NTB_BT",
      "PLSE_OFFER_BT",
      "MBLS_NTB_BT",
      "MBLS_OFFER_BT",
    ]),
  },
  UCF_SP: { value: "UCF_SP", types: new Set(["ZCUC", "ZCUC_NTB", "ZCUC_OFFER"]) },
  UCF_LAC: { value: "UCF_LAC", types: new Set(["ZLC", "ZLC_OFFER", "ZLC_NTB"]) },
  UCF_ALBT: { value: "UCF_ALBT", types: new Set(["ZBT", "ZBT_OFFER", "ZBT_NTB"]) },
  LAIP: { value: "LAIP", types: new Set(["HTS_LAIP"]) },
  CC: { value: "CC", types: new Set(["CC_RBL", "CC_DBS"]) },
  PLCARD: { value: "PLCARD", types: new Set(["PLCARD"]) },
  BEMFR: { value: "BEMFR", types: new Set(["BEMFR", "BEMFR_NTB"]) },
  MICRO_FINANCE: { value: "MICRO_FINANCE", types: new Set(["MICRO_FINANCE"]) },
  AASTANDA: { value: "AASTANDA", types: new Set(["AASTANDA"]) },
  BALIC: { value: "BALIC", types: new Set(["BALIC"]) },
  NCL: { value: "NCL", types: new Set(["NCL_PTB", "NCL_NTB", "NCL_ETB"]) },
  NTL_ALL: { value: "NTL_ALL", types: new Set(["NTL_ALL"]) },
  // Composite groupings (union of multiple product types)
  PROF: {
    value: "PROF",
    types: new Set([
      "PRO_GCO_DL",
      "PRO_NTB_DL",
      "PRO_ETB_DL",
      "RPR_NTB_DL",
      "PRO_DL",
      "PRO_ICL_DL",
      "RPR_TOPUP",
      "PEMFR_NTB",
      "PRO_GCO_CA",
      "PRO_NTB_CA",
      "PRO_ETB_CA",
      "PRO_ICL_CA",
      "PRO_LW",
      "PRO_NTB_LW",
      "PRO_ETB_LW",
    ]),
  },
  GL: { value: "GL", types: new Set(["GLFRESH", "GLBT"]) },
  MFI: { value: "MFI", types: new Set(["MICRO_FINANCE"]) },
  UCF: { value: "UCF", types: new Set(["UCF_SP", "UCF_LAC", "UCF_ALBT"]) },
  DC: { value: "DC", types: new Set(["LAPPROF_DC_NTB_DL"]) },
};

/**
 * Type for cart product names
 */
export type TypeCartProduct = keyof typeof PROD_CODES;

/**
 * Get product name from product code
 *
 * @param {string} prodCode - The product code
 * @returns {string | undefined} - The product name or undefined if not found
 */
export function getProductFromProdCode(prodCode: string): string | undefined {
  const [product] =
    Object.entries(PROD_CODES).find(([, value]) => value.types.has(prodCode.toUpperCase())) ?? [];
  return product;
}

/**
 * Retrieves cart items based on the provided product types.
 *
 * @param {ICartDetails} cartData - The details of the cart containing various items.
 * @param {TypeCartProduct[]} [products] - An optional array of product types to filter the cart items. If not provided, all cart items are returned.
 * @returns {ICartItemDetails[]} - An array of cart items that match the specified product types.
 */
export function getItemsInCart(
  cartData: ICartDetails,
  products?: TypeCartProduct[]
): ICartItemDetails[] {
  const foundCartItems: ICartItemDetails[] = [];
  if (!products) return cartData.cartDetails;

  for (const product of products) {
    const items = cartData.cartDetails?.filter((cartItem) =>
      PROD_CODES[product]?.types.has(cartItem.prod.toUpperCase())
    );
    if (items?.length) foundCartItems.push(...items);
  }
  return foundCartItems;
}

/**
 * Cart constants
 */
export const CART_CONSTANTS = {
  ALL: "ALL",
  WEB: "WEB",
};

/**
 * Default BALIC cart amount
 */
export const DEFAULT_BALIC_CART_AMOUNT = 1;
